import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MapGL, { Marker, Source, Layer, MapRef, NavigationControl } from 'react-map-gl/mapbox';
import { MAPBOX_TOKEN } from 'config/Mapbox';
import ProjectService from 'service/ProjectService';
import DashboardDataService from 'service/DashboardDataService';
import ProjectFilterDropdown, { ProjectItem } from 'component/ProjectFilterDropdown';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import { ProjectJobListResp } from 'domain/payload/response/project/ProjectJobListResp';
import { ProjectJobGeoListResp } from 'domain/payload/response/project/ProjectJobGeoListResp';
import { ProjectDetailResp } from 'domain/payload/response/project/ProjectDetailResp';
import { ProjectJobDetailResp } from 'domain/payload/response/project/ProjectJobDetailResp';
import { ProjectJobChartResp, ProjectJobChartItemResp } from 'domain/payload/response/project/ProjectJobChartResp';
import { ProjectTaskListResp } from 'domain/payload/response/project/ProjectTaskListResp';

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  grayText: '#64748b',
  darkText: '#1e293b',
};

const FieldDataMonitor: React.FC = () => {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state.auth.profile);
  const token = profile?.data?.token;

  const projectService = useMemo(() => ProjectService(), []);
  const dashboardDataService = useMemo(() => DashboardDataService(), []);

  // View state: 'list' or 'map'
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Filter states
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(routeProjectId || null);
  const [projectList, setProjectList] = useState<ProjectItem[]>([]);
  const [currentProject, setCurrentProject] = useState<ProjectDetailResp | null>(null);

  const [formId, setFormId] = useState<string | null>(null);
  const [formOptions, setFormOptions] = useState<DropdownOption[]>([]);

  const [tribeId, setTribeId] = useState<string | null>(null);
  const [tribeOptions, setTribeOptions] = useState<DropdownOption[]>([]);

  const [groupName, setGroupName] = useState<string | null>(null);
  const [groupOptions, setGroupOptions] = useState<DropdownOption[]>([]);

  // Status: '' (All), 'done', 'not_done'
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');

  // Pagination for List view
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Data states
  const [jobList, setJobList] = useState<ProjectJobListResp[]>([]);
  const [jobGeoList, setJobGeoList] = useState<ProjectJobGeoListResp[]>([]);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
  const [isLoadingGeo, setIsLoadingGeo] = useState<boolean>(false);

  // Summary counts data state (from projectJobChart API)
  const [chartData, setChartData] = useState<ProjectJobChartResp | null>(null);

  // Selected Job for Sidebar Content (Distribution Hub style)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<ProjectJobDetailResp | null>(null);
  const [selectedJobTasks, setSelectedJobTasks] = useState<ProjectTaskListResp[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);

  // Map state
  const mapRef = useRef<MapRef | null>(null);

  // Load custom SVG markers into Mapbox style as WebGL textures
  const loadMarkerImages = useCallback((map: any) => {
    if (!map) return;
    const markerConfigs = [
      { id: 'marker-green', src: '/assets/markers/ic_marker_green.svg' },
      { id: 'marker-red', src: '/assets/markers/ic_marker_red.svg' },
    ];

    markerConfigs.forEach(({ id, src }) => {
      if (map.hasImage(id)) return;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (map.hasImage(id)) return;
        const canvas = document.createElement('canvas');
        canvas.width = 141;
        canvas.height = 141;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, 141, 141);
          const imageData = ctx.getImageData(0, 0, 141, 141);
          if (!map.hasImage(id)) {
            map.addImage(id, imageData, { pixelRatio: 2 });
          }
        }
      };
      img.onerror = () => {
        map.loadImage(src, (err: any, image: any) => {
          if (!err && image && !map.hasImage(id)) {
            map.addImage(id, image);
          }
        });
      };
      img.src = src;
    });
  }, []);

  // WebGL GeoJSON FeatureCollection: converts 20k points into single GPU buffer (0 DOM HTML nodes)
  const geojsonData = useMemo(() => {
    return {
      type: 'FeatureCollection' as const,
      features: jobGeoList.map((item) => {
        const isDone = item.status === 'done' || item.tags?.includes('done');
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [item.longitude, item.latitude],
          },
          properties: {
            id: item.id,
            name: item.name,
            icon: isDone ? 'marker-green' : 'marker-red',
          },
        };
      }),
    };
  }, [jobGeoList]);

  // Selected item for highlighted overlay pin
  const selectedGeoItem = useMemo(() => {
    if (!selectedJobId) return null;
    return jobGeoList.find((item) => item.id === selectedJobId) || null;
  }, [jobGeoList, selectedJobId]);

  // Animated nav-pills indicators
  const { navRef: viewNavRef, indicatorStyle: viewIndicatorStyle } = useNavPillsIndicator(viewMode);
  const { navRef: statusNavRef, indicatorStyle: statusIndicatorStyle } = useNavPillsIndicator(statusFilter);

  // Resize map canvas when sidebar opens or closes (like Distribution Hub)
  useEffect(() => {
    if (viewMode === 'map') {
      const timer = setTimeout(() => {
        mapRef.current?.resize();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [selectedJobId, viewMode]);


  // Fetch project list
  useEffect(() => {
    if (!token) return;
    const fetchProjects = async () => {
      try {
        const res = await projectService.projectList(token, null, 1, 100, null);
        const mapped: ProjectItem[] = (res.data || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          isRunning: p.isRunning,
        }));
        setProjectList(mapped);

        if (!selectedProjectId && mapped.length > 0) {
          setSelectedProjectId(mapped[0].id);
        }
      } catch (err) {
        console.error('Failed to load project list:', err);
      }
    };
    fetchProjects();
  }, [token, projectService]);

  // Fetch filter options when project changes
  useEffect(() => {
    if (!token || !selectedProjectId) return;

    setFormId(null);
    setTribeId(null);
    setGroupName(null);
    setSelectedJobId(null);
    setCurrentPage(1);

    // Project detail
    projectService.projectDetail(token, selectedProjectId)
      .then((res) => {
        if (res.data) setCurrentProject(res.data);
      })
      .catch((err) => console.error('Failed to load project detail:', err));

    // Form options
    dashboardDataService.dashboardDataFormConfig(token, selectedProjectId)
      .then((res) => {
        const opts = (res.data || []).map((f: any) => ({
          id: f.id,
          name: f.name,
        }));
        setFormOptions(opts);
      })
      .catch((err) => console.error('Failed to load form config:', err));

    // Tribe options
    dashboardDataService.dashboardDataTribeConfig(token, selectedProjectId)
      .then((res) => {
        const opts = (res.data?.tribeList || []).map((t: any) => ({
          id: t.id,
          name: t.name,
        }));
        setTribeOptions(opts);
      })
      .catch((err) => console.error('Failed to load tribe config:', err));

    // Job group options
    projectService.projectJobGroupConfig(token, selectedProjectId)
      .then((res) => {
        const opts = (res.data || []).map((g: string) => ({
          id: g,
          name: g,
        }));
        setGroupOptions(opts);
      })
      .catch(() => {
        setGroupOptions([]);
      });
  }, [token, selectedProjectId, projectService, dashboardDataService]);

  // Fetch summary counts from dedicated API endpoint
  const fetchChartData = useCallback(async () => {
    if (!token || !selectedProjectId) return;
    try {
      const res = await projectService.projectJobChart(
        token,
        selectedProjectId,
        appliedSearch || null,
        formId,
        tribeId,
        groupName,
        statusFilter || null
      );
      setChartData(res.data ?? null);
    } catch (err: any) {
      console.error('Error fetching summary data:', err);
      setChartData(null);
    }
  }, [token, selectedProjectId, appliedSearch, formId, tribeId, groupName, statusFilter, projectService]);

  // Fetch Job List data (Table)
  const fetchTableData = useCallback(async () => {
    if (!token || !selectedProjectId) return;
    setIsLoadingList(true);
    try {
      const res = await projectService.projectJobList(
        token,
        selectedProjectId,
        statusFilter,
        appliedSearch || null,
        currentPage,
        pageSize,
        formId,
        tribeId,
        groupName
      );
      setJobList(res.data || []);
      setTotalRecords(res.pagination?.totalRecords || 0);
      setTotalPages(res.pagination?.totalPage || 1);
    } catch (err) {
      console.error('Failed to fetch job list:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [token, selectedProjectId, statusFilter, appliedSearch, currentPage, pageSize, formId, tribeId, groupName, projectService]);

  // Fetch Job Geo List data (Map)
  const fetchMapData = useCallback(async () => {
    if (!token || !selectedProjectId) return;
    setIsLoadingGeo(true);
    try {
      const res = await projectService.projectJobGeoList(
        token,
        selectedProjectId,
        statusFilter,
        appliedSearch || null,
        formId,
        tribeId,
        groupName
      );
      const validPoints = (res.data || []).filter(
        (j: any) => j.latitude != null && j.longitude != null && j.latitude !== 0 && j.longitude !== 0
      );
      setJobGeoList(validPoints);

      if (validPoints.length > 0 && mapRef.current) {
        let minLng = Infinity;
        let maxLng = -Infinity;
        let minLat = Infinity;
        let maxLat = -Infinity;
        for (let i = 0; i < validPoints.length; i++) {
          const pt = validPoints[i];
          if (pt.longitude < minLng) minLng = pt.longitude;
          if (pt.longitude > maxLng) maxLng = pt.longitude;
          if (pt.latitude < minLat) minLat = pt.latitude;
          if (pt.latitude > maxLat) maxLat = pt.latitude;
        }
        if (minLng !== Infinity && maxLng !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
          mapRef.current.fitBounds(
            [[minLng, minLat], [maxLng, maxLat]],
            { padding: 60, duration: 600 }
          );
        }
      }
    } catch (err) {
      console.error('Failed to fetch job geo list:', err);
    } finally {
      setIsLoadingGeo(false);
    }
  }, [token, selectedProjectId, statusFilter, appliedSearch, formId, tribeId, groupName, projectService]);

  // Fetch when tab or filters change
  useEffect(() => {
    fetchChartData();
    if (viewMode === 'list') {
      fetchTableData();
    } else {
      fetchMapData();
    }
  }, [viewMode, fetchChartData, fetchTableData, fetchMapData]);

  // Fetch Job Details and Tasks when selectedJobId changes
  useEffect(() => {
    if (!token || !selectedJobId) {
      setSelectedJobDetail(null);
      setSelectedJobTasks([]);
      return;
    }

    setIsLoadingDetail(true);
    projectService.projectJobDetail(token, selectedJobId)
      .then((res) => {
        setSelectedJobDetail(res.data || null);
      })
      .catch((err) => console.error('Failed to load job detail:', err))
      .finally(() => setIsLoadingDetail(false));

    setIsLoadingTasks(true);
    projectService.projectTaskList(token, selectedProjectId, selectedJobId, null, 1, 20)
      .then((res) => {
        setSelectedJobTasks(res.data || []);
      })
      .catch((err) => console.error('Failed to load job tasks:', err))
      .finally(() => setIsLoadingTasks(false));
  }, [token, selectedProjectId, selectedJobId, projectService]);

  // Search submit handler
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchTerm.trim());
    setCurrentPage(1);
  };

  const handleSelectJob = (id: string) => {
    setSelectedJobId(prev => prev === id ? null : id);
  };

  // Status metrics summary
  const statusItems: ProjectJobChartItemResp[] = chartData?.status ?? [];

  // Summary metrics
  const summaryMetrics = useMemo(() => {
    const total = chartData?.total ?? totalRecords;
    const doneItem = statusItems.find((s) => s.name === 'Done');
    const notDoneItem = statusItems.find((s) => s.name === 'Not Done');
    const done = doneItem ? doneItem.count : (currentProject?.resultCount?.done || 0);
    const notDone = notDoneItem ? notDoneItem.count : Math.max(0, total - done);
    return { total, done, notDone };
  }, [chartData, statusItems, totalRecords, currentProject]);

  // Find currently selected job from lists
  const currentSelectedJob = useMemo(() => {
    if (!selectedJobId) return null;
    return jobList.find((j) => j.id === selectedJobId) ||
      jobGeoList.find((j) => j.id === selectedJobId) ||
      (selectedJobDetail ? {
        id: selectedJobDetail.id,
        name: selectedJobDetail.name,
        groupName: selectedJobDetail.groupName,
        formName: '',
        placeName: selectedJobDetail.placeName,
        latitude: selectedJobDetail.latitude,
        longitude: selectedJobDetail.longitude,
        status: (selectedJobDetail.completeAssignmentCount > 0) ? 'done' : 'not_done'
      } : null);
  }, [selectedJobId, jobList, jobGeoList, selectedJobDetail]);

  // Dynamic additional info parser
  const parsedAdditionalInfo = useMemo(() => {
    if (!selectedJobDetail?.additionalInfo) return null;
    const raw = selectedJobDetail.additionalInfo;
    try {
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      } else if (typeof raw === 'object') {
        return raw;
      }
    } catch {
      // not json, return as single string entry
      return { Note: raw };
    }
    return null;
  }, [selectedJobDetail?.additionalInfo]);

  // Helper for task status badge
  const getTaskStatusBadge = (status: string) => {
    const s = String(status).toLowerCase();
    if (s.includes('complete') || s === '4' || s === 'approved' || s === 'done') {
      return <span className="badge bg-success">Completed</span>;
    }
    if (s.includes('failed') || s === '5' || s === 'rejected') {
      return <span className="badge bg-danger">Failed</span>;
    }
    return <span className="badge bg-primary">Active</span>;
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        {/* Page Header */}
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0 fw-bold">Field Data Monitor</h5>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                style={{ borderRadius: '8px', padding: '6px 14px' }}
                onClick={() => navigate(selectedProjectId ? `/monitor/${selectedProjectId}` : '/monitor/overview')}
              >
                <i className="fa-solid fa-arrow-left me-2"></i>
                Back to Field Monitor
              </button>
            </div>
            <div>
              <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                Field Data Monitor
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            {/* Top Filters (diatas) */}
            <div className="mb-3">
              <div className="row g-2 align-items-stretch">
                {/* Project Filter Dropdown */}
                <div className="col-12 col-md-auto" style={{ width: 240, maxHeight: 50 }}>
                  <ProjectFilterDropdown
                    projects={projectList}
                    defaultSelectedId={selectedProjectId === '' ? null : selectedProjectId}
                    allowAllProject={false}
                    onChange={(p) => {
                      if (p) {
                        setSelectedProjectId(p.id);
                        navigate(`/monitor/${p.id}/data`, { replace: true });
                      }
                    }}
                  />
                </div>

                {/* Form Name Filter */}
                {selectedProjectId && selectedProjectId !== '' && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={formId}
                      options={formOptions}
                      showDefaultOption={true}
                      defaultOptionLabel="All Form"
                      searchable={true}
                      onChange={(opt) => {
                        setFormId(opt.id != null && opt.id !== '' ? String(opt.id) : null);
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                    />
                  </div>
                )}

                {/* Tribe Name Filter */}
                {selectedProjectId && selectedProjectId !== '' && tribeOptions.length > 0 && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={tribeId}
                      options={tribeOptions}
                      showDefaultOption={true}
                      defaultOptionLabel="All Tribe"
                      searchable={true}
                      onChange={(opt) => {
                        setTribeId(opt.id != null && opt.id !== '' ? String(opt.id) : null);
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                    />
                  </div>
                )}

                {/* Job Group Filter */}
                {selectedProjectId && selectedProjectId !== '' && groupOptions.length > 0 && (
                  <div className="col-12 col-md-auto" style={{ height: 50, maxHeight: 50 }}>
                    <CustomDropdown
                      value={groupName}
                      options={groupOptions}
                      showDefaultOption={true}
                      defaultOptionLabel="All Group"
                      searchable={true}
                      onChange={(opt) => {
                        setGroupName(opt.id != null && opt.id !== '' ? String(opt.id) : null);
                        setCurrentPage(1);
                      }}
                      buttonStyle={{ width: 160 }}
                    />
                  </div>
                )}
              </div>
            </div>



            {/* Sub-bar: Summary info & Status Filter nav-pills */}
            {/* Lower Toolbar: View Switcher, Search Input + Button, Summary Metrics & Status Filter (kebawah) */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                {/* Left side: View Switcher & Search Bar */}
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {/* View Switcher: List vs Map */}
                  <div ref={viewNavRef} className="nav-pills-animated" style={{ height: 46 }}>
                    <div
                      className="nav-pill-indicator"
                      style={viewIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`h-100 nav-link ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      <i className="fa-solid fa-list me-2"></i>
                      List
                    </button>
                    <button
                      type="button"
                      className={`h-100 nav-link ${viewMode === 'map' ? 'active' : ''}`}
                      onClick={() => setViewMode('map')}
                    >
                      <i className="fa-solid fa-map-location-dot me-2"></i>
                      Map
                    </button>
                  </div>

                  {/* Search Input Form */}
                  <form className="d-flex" onSubmit={handleSearchSubmit} style={{ height: 46 }}>
                    <input
                      type="text"
                      className="form-control form-control-compact me-2 h-100"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ minWidth: 200 }}
                    />
                    <button type="submit" className="btn btn-primary px-3 h-100 d-flex align-items-center justify-content-center">
                      <i className="fa-solid fa-search"></i>
                    </button>
                  </form>
                </div>

                {/* Right side: Summary counts & Status Filter nav-pills */}
                <div className="d-flex align-items-center flex-wrap gap-3 ms-auto">
                  {/* Summary counts */}
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Total Data: <strong className="text-dark">{summaryMetrics.total.toLocaleString()}</strong>
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>•</span>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      <span className="text-success fw-bold">{summaryMetrics.done.toLocaleString()}</span> Done
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>•</span>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      <span className="text-danger fw-bold">{summaryMetrics.notDone.toLocaleString()}</span> Not Done
                    </span>
                  </div>

                  {/* Status Filter nav-pills */}
                  <div ref={statusNavRef} className="nav-pills-animated" style={{ height: 46 }}>
                    <div
                      className="nav-pill-indicator"
                      style={statusIndicatorStyle}
                    />
                    <button
                      type="button"
                      className={`h-100 nav-link ${statusFilter === '' ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter('');
                        setCurrentPage(1);
                      }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`h-100 nav-link ${statusFilter === 'done' ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter('done');
                        setCurrentPage(1);
                      }}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className={`h-100 nav-link ${statusFilter === 'not_done' ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter('not_done');
                        setCurrentPage(1);
                      }}
                    >
                      Not Done
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Split Grid Layout (Distribution Hub Pattern) */}
            <div className="row g-4">
              {/* Left Column: List or Map */}
              <div className={selectedJobId ? "col-12 col-xl-7" : "col-12"}>
                {viewMode === 'list' ? (
                  <>
                    <div className="table-responsive">
                      <table className="table table-condensed table-hover align-middle">
                        <thead className="table-primary">
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Place / Reference</th>
                            <th>Group</th>
                            <th>Form</th>
                            <th>Tribe</th>
                            <th>Worker / Installer</th>
                            <th>Coordinates</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {!isLoadingList ? (
                            jobList.length > 0 ? (
                              jobList.map((item) => {
                                const isDone = item.status === 'done' || (item.completeAssignmentCount != null && item.completeAssignmentCount > 0);
                                const isSelected = selectedJobId === item.id;
                                return (
                                  <tr
                                    key={item.id}
                                    className={isSelected ? 'table-secondary' : ''}
                                  >
                                    <td style={{ fontSize: '13px' }}>{item.id}</td>
                                    <td>
                                      <a
                                        href="#"
                                        className="fw-semibold text-decoration-none"
                                        style={{ fontSize: '13px' }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleSelectJob(item.id);
                                        }}
                                      >
                                        {item.name}
                                      </a>
                                    </td>
                                    <td style={{ fontSize: '13px' }}>{item.placeName || item.placeReferenceId || '-'}</td>
                                    <td style={{ fontSize: '13px' }}>{item.groupName || '-'}</td>
                                    <td style={{ fontSize: '13px' }}>{item.formName || '-'}</td>
                                    <td style={{ fontSize: '13px' }}>{item.tribeName || '-'}</td>
                                    <td style={{ fontSize: '13px' }}>
                                      {item.workerNames && item.workerNames.length > 0 ? (
                                        item.workerNames.join(', ')
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td style={{ fontSize: '13px' }}>
                                      {item.latitude && item.longitude ? (
                                        <a
                                          href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {Number(item.latitude).toFixed(5)}, {Number(item.longitude).toFixed(5)}
                                        </a>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td>
                                      <span
                                        className={`badge-status ${isDone ? 'bg-approved' : 'bg-rejected'}`}
                                        style={{ width: '85px', height: '26px', fontSize: '11.5px' }}
                                      >
                                        {isDone ? 'Done' : 'Not Done'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex" onClick={(e) => e.stopPropagation()}>
                                        <button
                                          className={`border-0 btn btn-small-compact d-flex align-items-center justify-content-center ${isSelected ? 'btn-info text-white' : 'btn-outline-info'}`}
                                          onClick={() => handleSelectJob(item.id)}
                                          title="View Details"
                                          style={{ width: '32px', height: '32px' }}
                                        >
                                          <i className="fa-solid fa-eye"></i>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={10} className="text-center py-4">
                                  No data available
                                </td>
                              </tr>
                            )
                          ) : (
                            <tr>
                              <td colSpan={10} className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="mt-3 d-flex justify-content-end align-items-center">
                      <div className="d-flex align-items-center">
                        <div className="me-4 d-flex align-items-center">
                          <label className="me-2">Rows per page:</label>
                          <CustomDropdown
                            value={pageSize}
                            options={PageSizeOption.options}
                            onChange={(opt) => {
                              setPageSize(Number(opt.id));
                              setCurrentPage(1);
                            }}
                            align="top"
                          />
                        </div>
                        <span className="me-3">
                          {totalRecords > 0
                            ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`
                            : '0 of 0'}
                        </span>

                        <nav>
                          <ul className="mb-0 pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                                <i className="fa fa-angle-double-left"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                                <i className="fa fa-angle-left"></i>
                              </button>
                            </li>

                            <li className="page-item disabled">
                              <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                            </li>

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                                <i className="fa fa-angle-right"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                                <i className="fa fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Map View with DIRECT MARKERS (No clusters) */
                  <div
                    className="position-relative w-100 rounded-3 overflow-hidden border"
                    style={{
                      height: 'calc(100vh - 270px)',
                      minHeight: '560px',
                      borderColor: '#e5e7eb',
                    }}
                  >
                    {isLoadingGeo && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.75)', zIndex: 10 }}
                      >
                        <div className="spinner-border text-primary mb-2" role="status"></div>
                        <span className="fw-semibold text-dark small">Loading location markers...</span>
                      </div>
                    )}

                    <MapGL
                      ref={mapRef}
                      mapboxAccessToken={MAPBOX_TOKEN}
                      initialViewState={{
                        longitude: 106.827153,
                        latitude: -6.175392,
                        zoom: 11,
                      }}
                      style={{ width: '100%', height: '100%' }}
                      mapStyle="mapbox://styles/mapbox/streets-v12"
                      interactiveLayerIds={['job-points-layer']}
                      onLoad={(e) => {
                        loadMarkerImages(e.target);
                      }}
                      onStyleData={() => {
                        if (mapRef.current) {
                          loadMarkerImages(mapRef.current.getMap());
                        }
                      }}
                      onClick={(e) => {
                        const feature = e.features?.[0];
                        if (feature && feature.properties?.id) {
                          const jobId = feature.properties.id;
                          handleSelectJob(jobId);
                          if (feature.geometry.type === 'Point') {
                            const coords = (feature.geometry as any).coordinates as [number, number];
                            mapRef.current?.flyTo({
                              center: [coords[0], coords[1]],
                              zoom: Math.max(mapRef.current.getZoom(), 13),
                              duration: 600,
                            });
                          }
                        }
                      }}
                      onMouseEnter={() => {
                        if (mapRef.current) mapRef.current.getCanvas().style.cursor = 'pointer';
                      }}
                      onMouseLeave={() => {
                        if (mapRef.current) mapRef.current.getCanvas().style.cursor = '';
                      }}
                    >
                      <NavigationControl position="bottom-right" />

                      {/* WebGL GeoJSON Symbol Layer: All points rendered on GPU at 60 FPS without clustering */}
                      <Source id="job-points-source" type="geojson" data={geojsonData}>
                        <Layer
                          id="job-points-layer"
                          type="symbol"
                          layout={{
                            'icon-image': ['get', 'icon'],
                            'icon-size': [
                              'interpolate',
                              ['linear'],
                              ['zoom'],
                              6, 0.12,
                              10, 0.16,
                              13, 0.22,
                              16, 0.26,
                            ],
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-anchor': 'bottom',
                          }}
                        />
                      </Source>

                      {/* Prominently highlighted marker overlay for the single selected job */}
                      {selectedGeoItem && (
                        <Marker
                          latitude={selectedGeoItem.latitude}
                          longitude={selectedGeoItem.longitude}
                          anchor="bottom"
                        >
                          <div
                            style={{
                              cursor: 'pointer',
                              transform: 'scale(1.35) translateY(-5px)',
                              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))',
                              transition: 'transform 0.2s ease',
                            }}
                            title={selectedGeoItem.name}
                          >
                            <img
                              src={
                                selectedGeoItem.status === 'done' || selectedGeoItem.tags?.includes('done')
                                  ? '/assets/markers/ic_marker_green.svg'
                                  : '/assets/markers/ic_marker_red.svg'
                              }
                              alt="Selected Job"
                              style={{ width: 34, height: 34, display: 'block' }}
                            />
                          </div>
                        </Marker>
                      )}
                    </MapGL>

                    {/* Direct Map Legend */}
                    <div
                      className="position-absolute bottom-0 start-0 m-3 p-2 bg-white rounded shadow-sm border d-flex flex-column gap-1"
                      style={{ zIndex: 5, fontSize: '0.8rem' }}
                    >
                      <div className="fw-bold text-dark" style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>
                        Legend
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <img src="/assets/markers/ic_marker_green.svg" alt="Done" style={{ width: 14, height: 14 }} />
                        <span className="text-muted">Done ({summaryMetrics.done})</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <img src="/assets/markers/ic_marker_red.svg" alt="Not Done" style={{ width: 14, height: 14 }} />
                        <span className="text-muted">Not Done ({summaryMetrics.notDone})</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Job Detail Sidebar (Distribution Hub style) */}
              {selectedJobId && (
                <div className="col-12 col-xl-5">
                  <div
                    className="card border rounded-3 shadow-sm p-4 bg-white h-100"
                    style={{ borderColor: '#e5e7eb', minHeight: '500px' }}
                  >
                    <div className="d-flex flex-column h-100">
                      {/* Sidebar Header */}
                      <div className="d-flex justify-content-between align-items-start mb-3 border-bottom pb-3">
                        <div className="pe-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span
                              className={`badge-status ${
                                currentSelectedJob?.status === 'done' || (selectedJobDetail?.completeAssignmentCount ?? 0) > 0
                                  ? 'bg-approved'
                                  : 'bg-rejected'
                              }`}
                              style={{ width: '85px', height: '24px', fontSize: '11px' }}
                            >
                              {currentSelectedJob?.status === 'done' || (selectedJobDetail?.completeAssignmentCount ?? 0) > 0
                                ? 'Done'
                                : 'Not Done'}
                            </span>
                            <span className="text-muted" style={{ fontSize: '12px' }}>
                              ID: <code>{selectedJobId}</code>
                            </span>
                          </div>
                          <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '17px' }}>
                            {selectedJobDetail?.name || currentSelectedJob?.name || 'Job Details'}
                          </h5>
                        </div>
                        <button
                          type="button"
                          className="btn btn-link text-muted p-0 text-decoration-none"
                          onClick={() => setSelectedJobId(null)}
                          aria-label="Close"
                          style={{ fontSize: '18px', lineHeight: 1 }}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>

                      {/* Sidebar Body */}
                      <div className="flex-grow-1 overflow-auto pe-1 d-flex flex-column gap-3" style={{ maxHeight: 'calc(100vh - 350px)' }}>
                        {isLoadingDetail ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                            <span className="text-muted small">Loading job details...</span>
                          </div>
                        ) : (
                          <>
                            {/* SECTION 1: BASIC INFORMATION */}
                            <div className="border rounded-3 p-3 bg-white" style={{ borderColor: '#e2e8f0' }}>
                              <div className="d-flex align-items-center mb-3">
                                <i className="fa-solid fa-circle-info me-2 text-primary" style={{ fontSize: '13px' }}></i>
                                <span
                                  className="fw-bold text-primary text-uppercase"
                                  style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                >
                                  BASIC INFORMATION
                                </span>
                              </div>

                              <div className="d-flex flex-column gap-2">
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Job Name</div>
                                  <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.name || currentSelectedJob?.name || '-'}
                                  </div>
                                </div>
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Description</div>
                                  <div className="col-8 text-dark" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.description || '-'}
                                  </div>
                                </div>
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Job Group</div>
                                  <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.groupName || currentSelectedJob?.groupName || '-'}
                                  </div>
                                </div>
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Place Name</div>
                                  <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.placeName || currentSelectedJob?.placeName || '-'}
                                  </div>
                                </div>
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Place Address</div>
                                  <div className="col-8 text-dark" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.placeAddress || '-'}
                                  </div>
                                </div>
                                <div className="row g-0 align-items-baseline">
                                  <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Coordinates</div>
                                  <div className="col-8" style={{ fontSize: '13px' }}>
                                    {selectedJobDetail?.latitude && selectedJobDetail?.longitude ? (
                                      <a
                                        href={`https://www.google.com/maps?q=${selectedJobDetail.latitude},${selectedJobDetail.longitude}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary text-decoration-none fw-semibold"
                                      >
                                        {Number(selectedJobDetail.latitude).toFixed(6)}, {Number(selectedJobDetail.longitude).toFixed(6)}
                                        <i className="fa-solid fa-arrow-up-right-from-square ms-1" style={{ fontSize: '11px' }}></i>
                                      </a>
                                    ) : (
                                      '-'
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* SECTION 2: DYNAMIC ADDITIONAL INFO */}
                            <div className="border rounded-3 p-3 bg-white" style={{ borderColor: '#e2e8f0' }}>
                              <div className="d-flex align-items-center mb-3">
                                <i className="fa-solid fa-sliders me-2 text-primary" style={{ fontSize: '13px' }}></i>
                                <span
                                  className="fw-bold text-primary text-uppercase"
                                  style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                >
                                  ADDITIONAL INFORMATION
                                </span>
                              </div>

                              {parsedAdditionalInfo && Object.keys(parsedAdditionalInfo).length > 0 ? (
                                <div className="d-flex flex-column gap-2">
                                  {Object.entries(parsedAdditionalInfo).map(([key, val]) => (
                                    <div
                                      key={key}
                                      className="p-2 rounded-2"
                                      style={{
                                        backgroundColor: '#F8FAFC',
                                        border: '1px solid #F1F5F9',
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: '#64748B',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.4px',
                                          marginBottom: '2px',
                                        }}
                                      >
                                        {key}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: '0.825rem',
                                          fontWeight: 600,
                                          color: '#0F172A',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '-')}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted small mb-0">No additional information recorded.</p>
                              )}
                            </div>

                            {/* SECTION 3: TASKS INFORMATION */}
                            <div className="border rounded-3 p-3 bg-white" style={{ borderColor: '#e2e8f0' }}>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                  <i className="fa-solid fa-list-check me-2 text-primary" style={{ fontSize: '13px' }}></i>
                                  <span
                                    className="fw-bold text-primary text-uppercase"
                                    style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                  >
                                    TASKS INFORMATION
                                  </span>
                                </div>
                                <div className="d-flex gap-1.5" style={{ fontSize: '11px' }}>
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">
                                    {selectedJobDetail?.completeAssignmentCount ?? 0} Done
                                  </span>
                                  <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                                    {selectedJobDetail?.activeAssignmentCount ?? 0} Active
                                  </span>
                                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25">
                                    {selectedJobDetail?.failedAssignmentCount ?? 0} Failed
                                  </span>
                                </div>
                              </div>

                              {isLoadingTasks ? (
                                <div className="text-center py-3">
                                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                </div>
                              ) : selectedJobTasks.length > 0 ? (
                                <div className="d-flex flex-column gap-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                  {selectedJobTasks.map((t) => (
                                    <div
                                      key={t.id}
                                      className="p-2.5 rounded-2 border bg-light d-flex justify-content-between align-items-center"
                                      style={{ fontSize: '12.5px' }}
                                    >
                                      <div>
                                        <div className="fw-semibold text-dark">{t.name}</div>
                                        <div className="text-muted small mt-0.5">
                                          <i className="fa-solid fa-user me-1 text-primary"></i>
                                          {t.installerName || 'Unassigned'}
                                          {t.createdAt && (
                                            <span className="ms-2">
                                              • {t.createdAt.substring(0, 10)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-end">
                                        {getTaskStatusBadge(t.status)}
                                        {t.resultCounts > 0 && (
                                          <div className="text-muted small mt-1">
                                            {t.resultCounts} result{t.resultCounts > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted small mb-0">No tasks currently recorded for this job.</p>
                              )}
                            </div>

                            {/* SECTION 4: RESULTS INFORMATION */}
                            <div className="border rounded-3 p-3 bg-white" style={{ borderColor: '#e2e8f0' }}>
                              <div className="d-flex align-items-center mb-3">
                                <i className="fa-solid fa-clipboard-check me-2 text-primary" style={{ fontSize: '13px' }}></i>
                                <span
                                  className="fw-bold text-primary text-uppercase"
                                  style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                >
                                  RESULTS INFORMATION
                                </span>
                              </div>

                              <div className="row g-2 text-center">
                                <div className="col-3">
                                  <div className="p-2 border rounded bg-light">
                                    <span className="text-muted small d-block" style={{ fontSize: '11px' }}>Approved</span>
                                    <strong className="text-success" style={{ fontSize: '1rem' }}>
                                      {selectedJobDetail?.resultApproveCount ?? 0}
                                    </strong>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="p-2 border rounded bg-light">
                                    <span className="text-muted small d-block" style={{ fontSize: '11px' }}>QC</span>
                                    <strong className="text-warning" style={{ fontSize: '1rem' }}>
                                      {selectedJobDetail?.resultQcCount ?? 0}
                                    </strong>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="p-2 border rounded bg-light">
                                    <span className="text-muted small d-block" style={{ fontSize: '11px' }}>Rejected</span>
                                    <strong className="text-danger" style={{ fontSize: '1rem' }}>
                                      {selectedJobDetail?.resultRejectCount ?? 0}
                                    </strong>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="p-2 border rounded bg-light">
                                    <span className="text-muted small d-block" style={{ fontSize: '11px' }}>Total</span>
                                    <strong className="text-primary" style={{ fontSize: '1rem' }}>
                                      {(selectedJobDetail?.resultApproveCount ?? 0) +
                                        (selectedJobDetail?.resultQcCount ?? 0) +
                                        (selectedJobDetail?.resultRejectCount ?? 0)}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Sidebar Footer */}
                      <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-2 border-top">
                        <button
                          type="button"
                          className="btn btn-outline-secondary px-3 py-1"
                          style={{ fontSize: '13px' }}
                          onClick={() => setSelectedJobId(null)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDataMonitor;
